import { Component as BaseComponent, h } from 'preact';
import { connect } from 'react-redux';
import { isValidEmail } from '../../../shared/util';
import { MediaQuery as MQ } from '../../../shared/media-query';
import { Suspense, lazy } from 'preact/compat';
const InputGroup = lazy(
    () => import(/* webpackChunkName: "input-group" */ '../../../shared/form/input-group')
);

class Component extends BaseComponent {
    constructor(props) {
        super(props);

        this.devcat = MQ.isMobile() ? 'Mobile' : 'Desktop';
        this.ctx = (props.signupIntent && props.signupIntent.ctx)
            ? props.signupIntent.ctx
            : (window.location.href.includes('readings-reports') ? 'reading' : 'nav');

        if (props.user && props.user.logged_in) {
            props.close();
        } else {
            props.trackEvent([
                'Open',
                'SiteInteraction:Login',
            ]);

            this.state = {
                email: {
                    value: props.user && props.user.email,
                    error: null,
                },
                confirm_email: {
                    value: null,
                    error: null,
                },
                password: {
                    value: null,
                    error: null,
                },
            };
        }
    }

    componentDidMount() {
        this.props.trackCustomEvent({
            event_name: 'login_view',
            params: {
                devcat: this.devcat,
                ctx: this.ctx,
            },
        });

        // Sync passwordError if it's already set when component mounts
        if (this.props.passwordError) {
            this.setState({
                password: {
                    value: this.state.password.value,
                    error: this.props.passwordError,
                },
            });
        }

        this.initializeGoogleSignIn();

        this.initializeAppleSignIn();
    }

    componentDidUpdate(prevProps) {
        // Sync passwordError from props to local state when it appears
        if (this.props.passwordError && !prevProps.passwordError) {
            this.setState({
                password: {
                    value: this.state.password.value,
                    error: this.props.passwordError,
                },
            });
        }

        // Sync email from props to local state if it changes
        if (this.props.user && this.props.user.email &&
            this.state.email.value !== this.props.user.email) {
            this.setState({
                email: {
                    value: this.props.user.email,
                    error: null,
                },
            });
        }
    }

    componentWillUnmount() {
        this.props.userUpdate({
            emailError: null,
            passwordError: null,
        });
    }

    initializeGoogleSignIn = () => {
        const maxRetries = 3;
        const retryDelay = 500; // 500ms
        let retryCount = 0;

        const tryInitializeGoogle = () => {
            if (window.google && window.google.accounts && window.google.accounts.id) {
                window.google.accounts.id.initialize({
                    client_id: this.props.google && this.props.google.client_id,
                    callback: this.googleSignin,
                    auto_select: false,
                    context: 'signin',
                    ux_mode: 'popup',
                });

                window.google.accounts.id.renderButton(
                    document.getElementById('google-signin-button'),
                    {
                        type: 'standard',
                        theme: 'outline',
                        size: 'large',
                        text: 'signin',
                        shape: 'rectangular',
                        logo_alignment: 'left',
                        width: '100',
                    }
                );
            } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(tryInitializeGoogle, retryDelay);
            }
        };

        tryInitializeGoogle();
    }

    initializeAppleSignIn = () => {
        if (window.AppleID && window.AppleID.auth) {
            window.AppleID.auth.init({
                clientId: this.props.apple && this.props.apple.client_id,
                scope: 'name email',
                redirectURI: window.location.origin,
                usePopup: true,
            });
        }
    }

    render({
        close,
        emailError,
        passwordError,
        user,
    }, {
        confirm_email,
        email,
        password,
    }) {
        if (user && !user.logged_in) {
            let title = 'Log In';
            let button_text = 'Log In';

            return (
                <login-modal>
                    <row className='has-padding-15'>
                        <column className='is-2-tablet is-hidden-mobile is-hidden-desktop' />
                        <column className='is-12-mobile is-8-tablet is-5'>
                            <dialog-header>
                                <row className='has-margin-15-bottom'>
                                    <column className='is-6 login-logo'>
                                        <img
                                            src='https://gfx.tarot.com/images/navigation/icons/navigation-icons-newlogo-full.svg' // eslint-disable-line max-len
                                            alt='Tarot.com'
                                        />
                                    </column>
                                    <button className='close-icon' onClick={close} />
                                </row>
                                <h1 className='signup-title' dangerouslySetInnerHTML={{ __html: title }} />
                                <p className='signup-subtitle'>
                                    Need an account?{' '}
                                    <a href='#signup' className='scribble-link'>
                                        <b>Sign up for free.</b>
                                        <img
                                            src='https://gfx.tarot.com/images/signup/scribble-underline.svg'
                                            alt='Scribble underline'
                                            className='scribble-underline'
                                        />
                                    </a>
                                </p>
                            </dialog-header>
                            <dialog-content>
                                <form autoComplete='on' onSubmit={this.validate}>
                                    <row className='confirm_row'>
                                        <column className='is-12'>
                                            <Suspense fallback={<div className='skeleton' />}>
                                                <InputGroup
                                                    type='text'
                                                    name='confirm_email'
                                                    label='Confirm Your Email'
                                                    value={confirm_email.value}
                                                    onInput={this.setFormValues}
                                                />
                                            </Suspense>
                                        </column>
                                    </row>
                                    <row>
                                        <column className='is-12'>
                                            <Suspense fallback={<div className='skeleton' />}>
                                                <InputGroup
                                                    autoFocus
                                                    type='email-placeholder'
                                                    name='email'
                                                    placeholder='Email'
                                                    value={email.value}
                                                    error={email.error || emailError}
                                                    onInput={this.setFormValues}
                                                />
                                            </Suspense>
                                        </column>
                                    </row>
                                    <row className='has-margin-20-bottom'>
                                        <column className='is-12'>
                                            <Suspense fallback={<div className='skeleton' />}>
                                                <InputGroup
                                                    type='password-placeholder'
                                                    name='password'
                                                    placeholder='Password'
                                                    value={password.value}
                                                    error={password.error || passwordError}
                                                    onInput={this.setFormValues}
                                                />
                                            </Suspense>
                                        </column>
                                    </row>
                                    <row className='has-margin-25-bottom'>
                                        <column className='is-12 has-text-center'>
                                            <button
                                                className='next-button'
                                                onClick={this.validate}
                                            >
                                                {button_text}
                                            </button>
                                        </column>
                                    </row>
                                    <row>
                                        <column className='is-12'>
                                            <a
                                                href='/password-reset'
                                                onClick={() => this.trackClick(
                                                    'Modal_LogInSignUp:ForgotYourPassword'
                                                )}
                                                className='forgot-password'
                                            >
                                                Forgot Email or Password?
                                            </a>
                                        </column>
                                    </row>
                                    <row>
                                        <column className='is-12'>
                                            <div className='separator'>
                                                <span>or continue with</span>
                                            </div>
                                        </column>
                                    </row>
                                    <row>
                                        <column className='is-12'>
                                            <div className='sso-buttons'>
                                                <div id='google-signin-button' />
                                                <button
                                                    type='button'
                                                    className='sso-button facebook'
                                                    onClick={this.handleFacebookLogin}
                                                >
                                                    <span className='sso-icon' />
                                                    Facebook
                                                </button>
                                                <button
                                                    type='button'
                                                    className='sso-button apple'
                                                    onClick={this.handleAppleLogin}
                                                >
                                                    <span className='sso-icon' />
                                                    Apple
                                                </button>
                                            </div>
                                        </column>
                                    </row>
                                </form>
                            </dialog-content>
                        </column>
                        <column className='is-hidden-mobile is-hidden-tablet is-7'>
                            <div className='signup-promo'>
                                <div className='video-container'>
                                    <video
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        poster='https://gfx.tarot.com/images/signup/tarot-reader-fallback.webp'
                                    >
                                        <source
                                            src='https://gfx.tarot.com/images/signup/tarot-reader-vid.mp4'
                                            type='video/mp4'
                                        />
                                    </video>
                                </div>
                            </div>
                        </column>
                    </row>
                </login-modal>
            );
        }

        return null;
    }

    setFormValues = (e) => {
        let state = {};

        if ((e.type == 'keyup' && e.key == 'Enter') || e.type == 'change' || e.type == 'input') {
            state[e.target.name] = {
                value: e.target.value,
                error: null,
            };

            this.setState(state);
        }
    }

    validate = (e) => {
        e.preventDefault();

        const state = {
            email: {
                value: this.state.email.value,
                error: isValidEmail(this.state.email.value) ? null : 'Please enter a valid email',
            },
            password: {
                value: this.state.password.value,
                error: this.state.password.value ? this.validatePassword() : 'Please enter a password',
            },
            confirm_email: {
                value: this.state.confirm_email.value || document.querySelector('input[name="confirm_email"]').value,
                error: null,
            },
        };

        this.setState(state);

        if (
            state.confirm_email.value
            ||
            document.querySelector('input[name="confirm_email"]').value
        ) {
            this.props.trackEvent([
                '#login',
                'Bot:Attempted',
            ]);
        }

        if (
            !state.email.error
            &&
            !state.password.error
            &&
            !state.confirm_email.value
            &&
            !document.querySelector('input[name="confirm_email"]').value
        ) {
            this.props.trackCustomEvent({
                event_name: 'login_start',
                params: {
                    devcat: this.devcat,
                    meth: 'password',
                },
            });
            this.props.userLogin({
                email: this.state.email.value,
                password: this.state.password.value,
            });
        }
    }

    validatePassword = () => {
        return this.state.password.value.length > 3 ? null : 'Please enter a password longer than 3 characters.';
    }

    trackClick = (info) => {
        this.props.trackEvent(['Click', info]);
    }

    handleFacebookLogin = () => {
        this.props.trackCustomEvent({
            event_name: 'login_start',
            params: {
                devcat: this.devcat,
                meth: 'facebook',
            },
        });

        if (window.FB) {
            window.FB.login((response) => {
                if (response.authResponse) {
                    window.FB.api(
                        '/me',
                        { fields: 'name,email' },
                        (profileResponse) => {
                            if (profileResponse && !profileResponse.error) {
                                this.props.userLogin({
                                    sso: 'facebook',
                                    credential: response.authResponse.accessToken,
                                    user_id: response.authResponse.userID,
                                    email: profileResponse.email,
                                });
                            }
                        }
                    );
                }
                else {
                    this.props.trackCustomEvent({
                        event_name: 'login_error',
                        params: {
                            devcat: this.devcat,
                            meth: 'facebook',
                            ctx: this.ctx,
                            error_type: JSON.stringify(response),
                        },
                    });
                }
            }, { scope: 'public_profile,email' });
        }
    }

    googleSignin = (response) => {
        this.props.trackCustomEvent({
            event_name: 'login_start',
            params: {
                devcat: this.devcat,
                meth: 'google',
            },
        });

        this.props.userLogin({
            credential: response.credential,
            sso: 'google',
        });
    }

    decodeJwtResponse = (token) => {
        let base64Url = token.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    handleAppleLogin = () => {
        this.props.trackCustomEvent({
            event_name: 'login_start',
            params: {
                devcat: this.devcat,
                meth: 'apple',
            },
        });

        if (window.AppleID && window.AppleID.auth) {
            window.AppleID.auth.signIn()
                .then((response) => {
                    if (response && response.authorization) {
                        const responsePayload = this.decodeJwtResponse(response.authorization.id_token);
                        this.props.userLogin({
                            sso: 'apple',
                            credential: response.authorization.id_token,
                            email: responsePayload.email,
                        });
                    }
                })
                .catch((error) => {
                    this.props.trackCustomEvent({
                        event_name: 'login_error',
                        params: {
                            devcat: this.devcat,
                            meth: 'apple',
                            ctx: this.ctx,
                            error_type: JSON.stringify(error.error),
                        },
                    });
                });
        }
    }
}

const mapStateToProps = (state) => ({
    user: state.user,
    emailError: state.user && state.user.emailError,
    passwordError: state.user && typeof state.user.passwordError === 'boolean'
        ? 'Password did not match this email'
        : state.user.passwordError,
    google: state.google,
    apple: state.apple,
    signupIntent: state['signup-intent'],
});

const mapDispatchToProps = (dispatch) => ({
    trackEvent: (payload) => dispatch({ type: 'ga:event', payload: payload }),
    trackCustomEvent: (payload) => dispatch({ type: 'ga:custom_event', payload: payload }),
    userLogin: (payload) => dispatch({ type: 'user:login', payload: payload }),
    userUpdate: (payload) => dispatch({ type: 'user:update', payload: payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Component);
